import { type FormEvent, useState } from 'react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { cn } from '@/components/ui/cn'
import { apiClient } from '@/api/client'
import { useMinCopy } from '@/i18n/useUiLocale'
import { useAuthStore } from '@/store/authStore'

type FeedbackCategory = 'LIKE' | 'DISLIKE' | 'BUG' | 'IDEA'

type FeedbackFormProps = {
  pagePath?: string
  requireEmailWhenGuest?: boolean
  className?: string
  /** Prefer guest locale (FAQ public page). */
  preferGuestLocale?: boolean
}

export function FeedbackForm({
  pagePath,
  requireEmailWhenGuest = true,
  className,
  preferGuestLocale = false,
}: FeedbackFormProps) {
  const isAuthenticated = useAuthStore((s) => Boolean(s.accessToken))
  const feedback = useMinCopy({ preferGuest: preferGuestLocale || !isAuthenticated }).feedback
  const [category, setCategory] = useState<FeedbackCategory>('LIKE')
  const [message, setMessage] = useState('')
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle')

  const categories: { id: FeedbackCategory; label: string }[] = [
    { id: 'LIKE', label: feedback.like },
    { id: 'DISLIKE', label: feedback.dislike },
    { id: 'BUG', label: feedback.bug },
    { id: 'IDEA', label: feedback.idea },
  ]

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (status === 'loading' || message.trim().length < 5) return
    if (!isAuthenticated && requireEmailWhenGuest && !email.trim()) return

    setStatus('loading')
    try {
      await apiClient.post('/feedback', {
        category,
        message: message.trim(),
        email: isAuthenticated ? undefined : email.trim() || undefined,
        pagePath: pagePath ?? (typeof window !== 'undefined' ? window.location.pathname : undefined),
      })
      setStatus('done')
      setMessage('')
    } catch {
      setStatus('error')
    }
  }

  if (status === 'done') {
    return (
      <div className={cn('space-y-2', className)} data-testid="feedback-done">
        <p className="text-sm text-emerald-700 dark:text-emerald-400">{feedback.done}</p>
        <button
          type="button"
          onClick={() => setStatus('idle')}
          className="text-sm font-medium text-primary underline-offset-2 hover:underline"
          data-testid="feedback-send-another"
        >
          {feedback.sendAnother}
        </button>
      </div>
    )
  }

  return (
    <form
      onSubmit={onSubmit}
      className={cn('space-y-3', className)}
      data-testid="feedback-form"
    >
      <div className="flex flex-wrap gap-2">
        {categories.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setCategory(item.id)}
            className={cn(
              'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
              category === item.id
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border text-muted-foreground hover:border-primary/40 hover:text-foreground',
            )}
          >
            {item.label}
          </button>
        ))}
      </div>

      <textarea
        required
        minLength={5}
        maxLength={2000}
        rows={4}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder={feedback.placeholder}
        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label={feedback.placeholder}
      />

      {!isAuthenticated && requireEmailWhenGuest ? (
        <Input
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={feedback.emailPlaceholder}
          aria-label={feedback.emailPlaceholder}
        />
      ) : null}

      <Button type="submit" disabled={status === 'loading'} className="w-full sm:w-auto">
        {status === 'loading' ? '…' : feedback.submit}
      </Button>

      {status === 'error' ? (
        <p className="text-xs text-destructive">{feedback.error}</p>
      ) : null}
    </form>
  )
}
